import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { ImmersiveVocabularyCards } from './ImmersiveVocabularyCards';
import type { ActiveVocabularyTermData } from './ImmersiveVocabularyCards';
import { TimeDrivenTranscript } from './TimeDrivenTranscript';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, GraduationCap, Loader2, MessageCircle, Quote, ClipboardCopy, Check, Sparkles, Lightbulb } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { useYoudaoTranslation } from "@/hooks/use-youdao-translation";
import { getSpeakerLang } from "@/utils/device-detection";
import { useWordExplanation } from "@/hooks/use-word-explanation";
import { vocabularyLevelChecker } from "@/lib/vocabularyLevel";
import { useI18n } from "@/lib/i18n";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { createVocabularyTermKey } from "@/lib/vocabulary-term-key";

const STATUS_DEFINITIONS = [
  {
    value: 'NEW',
    labelKey: 'mobileImmersive.status.new',
    descriptionKey: 'mobileImmersive.status.newDescription',
    fallbackLabel: 'New',
    fallbackDescription: 'Just added, needs first review',
  },
  {
    value: 'FUZZY',
    labelKey: 'mobileImmersive.status.fuzzy',
    descriptionKey: 'mobileImmersive.status.fuzzyDescription',
    fallbackLabel: 'Fuzzy',
    fallbackDescription: 'Needs more practice soon',
  },
  {
    value: 'MASTERED',
    labelKey: 'mobileImmersive.status.mastered',
    descriptionKey: 'mobileImmersive.status.masteredDescription',
    fallbackLabel: 'Mastered',
    fallbackDescription: 'Confident and on long review',
  },
] as const;

type StatusValue = typeof STATUS_DEFINITIONS[number]['value'];

interface StatusOption {
  value: StatusValue;
  label: string;
  description: string;
}

const statusStyles: Record<StatusValue, string> = {
  NEW: 'bg-sky-100 text-sky-700 border-sky-200',
  FUZZY: 'bg-amber-100 text-amber-700 border-amber-200',
  MASTERED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const statusAccentMap: Record<StatusValue, string> = {
  NEW: 'bg-sky-400',
  FUZZY: 'bg-amber-400',
  MASTERED: 'bg-emerald-400',
};

interface MobileImmersiveContentProps {
  // Vocabulary props
  vocabulary?: any;
  currentTime: number;
  onTimestampClick: (timestamp: number, endTimestamp?: number) => void;
  onActiveVocabularyTermsChange?: (terms: ActiveVocabularyTermData[]) => void;
  showTranslation?: boolean;
  revealAllTranslations?: boolean;
  
  // Transcript props
  groupedSentences: any[];
  ytPlayer: any;
  userSettings: any;
  wordHighlightEnabled: boolean;
  practiceMode: boolean;
  practiceModeType: "vocabulary" | "toefl" | "shadowing";
  vocabularyTerms: string[];
  setIsPlaying: (playing: boolean) => void;
  isWordCheckerReady: boolean;
  episodeId?: string;
  activeSubtitleId?: string | null;
  onActiveSubtitleChange?: (
    subtitleId: string | null,
    subtitleText?: string,
    subtitleTranslation?: string,
    subtitleTimestamp?: [number, number]
  ) => void;
  
  // Subtitle props for mobile immersive mode
  activeSubtitleText?: string;
  activeSubtitleTranslation?: string;
  activeVocabularyTerms?: ActiveVocabularyTermData[];
  hiddenVocabularyTermKeys?: string[];
  showSubtitles?: boolean;
  
  // Authentication
  session?: any;
  hasTwoBrokeGirlsAccess?: boolean;
  isTwoBrokeGirlsEpisode?: boolean;
  onTranscriptUpdate?: (transcript: any[], audioUrl?: string) => void;
}

export function MobileImmersiveContent({
  vocabulary,
  currentTime,
  onTimestampClick,
  onActiveVocabularyTermsChange,
  showTranslation = false,
  revealAllTranslations = false,
  groupedSentences,
  ytPlayer: _ytPlayer,
  userSettings,
  wordHighlightEnabled: _wordHighlightEnabled,
  practiceMode: _practiceMode,
  practiceModeType: _practiceModeType,
  vocabularyTerms,
  setIsPlaying: _setIsPlaying,
  isWordCheckerReady: _isWordCheckerReady,
  episodeId,
  activeSubtitleId,
  onActiveSubtitleChange: _onActiveSubtitleChange,
  activeSubtitleText,
  activeSubtitleTranslation,
  activeVocabularyTerms = [],
  hiddenVocabularyTermKeys = [],
  showSubtitles = true,
  session,
  hasTwoBrokeGirlsAccess: _hasTwoBrokeGirlsAccess = false,
  isTwoBrokeGirlsEpisode: _isTwoBrokeGirlsEpisode = false,
  onTranscriptUpdate,
}: MobileImmersiveContentProps) {
void [
  _ytPlayer,
  _wordHighlightEnabled,
  _practiceMode,
  _practiceModeType,
  _setIsPlaying,
  _isWordCheckerReady,
  _onActiveSubtitleChange,
  _hasTwoBrokeGirlsAccess,
  _isTwoBrokeGirlsEpisode,
];
  
  // ===== 单词交互状态 =====
  const [selectedWord, setSelectedWord] = useState('');
  const [selectedWordContext, setSelectedWordContext] = useState('');
  const [showWordDrawer, setShowWordDrawer] = useState(false);
  const [selectedStatusValue, setSelectedStatusValue] = useState<StatusValue>('NEW');
  const speakerRef = useRef<HTMLDivElement>(null);
  const highlightedTermsRef = useRef<Map<string, string>>(new Map());
  
  // ===== 有道翻译Hook =====
  const { translations, loading: youdaoLoading, getTranslation, isInitialized } = useYoudaoTranslation();
  const { t } = useI18n();
  const {
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
  } = useWordExplanation(userSettings, selectedWordContext, session?.user?.id);

  const statusOptions = useMemo<StatusOption[]>(() =>
    STATUS_DEFINITIONS.map((definition) => ({
      value: definition.value,
      label: t(definition.labelKey, definition.fallbackLabel),
      description: t(definition.descriptionKey, definition.fallbackDescription),
    })),
  [t]);

  const selectedStatusMobile = useMemo<StatusOption>(() => {
    const fallback: StatusOption = statusOptions[0] ?? {
      value: 'NEW',
      label: t('mobileImmersive.status.new', 'New'),
      description: t('mobileImmersive.status.newDescription', 'Just added, needs first review'),
    };
    return statusOptions.find((option) => option.value === selectedStatusValue) ?? fallback;
  }, [statusOptions, selectedStatusValue, t]);

  const explanation = selectedWord ? explanations[selectedWord] : undefined;
  const translation = selectedWord ? translations[selectedWord] : undefined;
  const lookupMetadata = explanation?.lookupMetadata;
  const hasLookupMetadata = Boolean(
    lookupMetadata && (
      (typeof lookupMetadata.definition === 'string' && lookupMetadata.definition.trim())
      || lookupMetadata.tag
      || (typeof lookupMetadata.bnc === 'number' && lookupMetadata.bnc > 0)
      || (typeof lookupMetadata.frq === 'number' && lookupMetadata.frq > 0)
    )
  );
  const lookupTags = useMemo(() => {
    if (!lookupMetadata?.tag) return [] as string[];
    return lookupMetadata.tag
      .split(/[,\s]+/)
      .map((token) => token.trim())
      .filter(Boolean);
  }, [lookupMetadata?.tag]);
  const isWordLoading = selectedWord ? (loading[selectedWord] || youdaoLoading[selectedWord]) : false;
  const isWordSaving = selectedWord ? savingWord[selectedWord] : false;
  const isWordSaved = selectedWord ? isCardSaved(selectedWord) : false;
  const StatusIconMobile = selectedStatusMobile?.value === 'MASTERED'
    ? Check
    : selectedStatusMobile?.value === 'FUZZY'
      ? Lightbulb
      : Sparkles;

  const filteredVocabulary = useMemo(() => {
    if (!vocabulary) {
      return vocabulary;
    }

    if (!hiddenVocabularyTermKeys || hiddenVocabularyTermKeys.length === 0) {
      return vocabulary;
    }

    if (!Array.isArray(vocabulary.scenarios)) {
      return vocabulary;
    }

    const hiddenSet = new Set(hiddenVocabularyTermKeys);
    let didFilter = false;

    const scenarios = vocabulary.scenarios
      .map((scenario: any) => {
        if (!scenario) {
          return scenario;
        }

        const originalTerms = Array.isArray(scenario.terms) ? scenario.terms : [];
        const filteredTerms = originalTerms.filter((term: any) => {
          const key = createVocabularyTermKey(term?.term, scenario?.name);
          if (!key) {
            return true;
          }
          if (hiddenSet.has(key)) {
            didFilter = true;
            return false;
          }
          return true;
        });

        if (filteredTerms.length !== originalTerms.length) {
          didFilter = true;
        }

        return {
          ...scenario,
          terms: filteredTerms,
        };
      })
      .filter(
        (scenario: any) =>
          Array.isArray(scenario?.terms) && scenario.terms.length > 0
      );

    if (!didFilter && scenarios.length === vocabulary.scenarios.length) {
      return vocabulary;
    }

    return {
      ...vocabulary,
      scenarios,
    };
  }, [vocabulary, hiddenVocabularyTermKeys]);

  const hasVocabularyToDisplay = useMemo(() => {
    if (!filteredVocabulary?.scenarios) {
      return false;
    }

    return filteredVocabulary.scenarios.some(
      (scenario: any) =>
        Array.isArray(scenario?.terms) && scenario.terms.length > 0
    );
  }, [filteredVocabulary]);

  const showVocabularyCards = Boolean(vocabulary) && hasVocabularyToDisplay;
  const showLoadingState = !vocabulary;
  const showEmptyState = Boolean(vocabulary) && !hasVocabularyToDisplay;
  
  // ===== 单词点击处理 =====
  const handleWordClick = useCallback(async (
    word: string,
    event: React.MouseEvent | React.TouchEvent,
    contextText: string
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setSelectedWord(word);
    setSelectedWordContext(contextText);
    setSelectedStatusValue('NEW');
    setShowWordDrawer(true);

    let hasLocal = false;
    try {
      hasLocal = await vocabularyLevelChecker.hasTranslationAsync(word.toLowerCase());
    } catch (error) {
      console.warn('Failed to check local vocabulary:', word, error);
    }

    await getExplanation(word);

    if (!hasLocal && isInitialized && getTranslation) {
      getTranslation(word);
    }
  }, [getExplanation, getTranslation, isInitialized]);
  
  // ===== 检查是否为有效英文单词 =====
  const isValidEnglishWord = useCallback((word: string) => {
    // 检查缩写
    const isContraction = /^'?(s|m|ve|ll|re|d)$/i.test(word);
    if (isContraction) return false;

    // 检查是否为有效英文单词
    const isValidWord = /^[a-zA-Z]+('[a-zA-Z]+)?$/i.test(word);
    return isValidWord;
  }, []);

  useEffect(() => {
    highlightedTermsRef.current.clear();
  }, [episodeId]);
  
  // ===== 高亮字幕中的vocabulary terms =====
  const highlightTermsInSubtitle = useMemo(() => {
    const suffixes = ['ed', 'ing', 's', 'er', 'est', 'ly'];

    const createFlexiblePattern = (terms: ActiveVocabularyTermData[]) => {
      const patterns = terms.map(({ term }) => {
        if (!term) return '';

        if (term.includes(' ')) {
          const words = term.split(' ');
          const flexibleWords = words.map((word) => {
            const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return `${escapedWord}(?:ed|ing|s|er|est|ly)?`;
          });
          return flexibleWords.join('\\s+');
        }

        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return `${escapedTerm}(?:ed|ing|s|er|est|ly)?`;
      });

      return patterns.filter(Boolean).join('|');
    };

    const matchesTerm = (segment: string, entry: ActiveVocabularyTermData) => {
      const term = entry?.term;
      if (!term) return false;

      const partLower = segment.toLowerCase();
      const termLower = term.toLowerCase();

      if (partLower === termLower) return true;

      const matchesWithSuffix = (base: string, target: string) => {
        if (!target) return false;

        if (target === base) {
          return true;
        }

        return suffixes.some((suffix) =>
          target === `${base}${suffix}` || (target.startsWith(base) && target.endsWith(suffix))
        );
      };

      if (term.includes(' ')) {
        const termWords = termLower.split(' ');
        const partWords = partLower.trim().split(/\s+/);

        if (termWords.length !== partWords.length) return false;

        return termWords.every((termWord, index) => matchesWithSuffix(termWord, partWords[index] ?? ''));
      }

      return matchesWithSuffix(termLower, partLower);
    };

    const renderTokens = (segment: string, contextText: string, keyPrefix: string) => {
      const tokens = segment.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b|\d+%?|\s+|[^\w\s]/g) || [segment];

      return tokens.map((token, tokenIndex) => {
        const isWord = isValidEnglishWord(token);

        if (!isWord) {
          return <span key={`${keyPrefix}-${tokenIndex}`}>{token}</span>;
        }

        return (
          <span
            key={`${keyPrefix}-${tokenIndex}`}
            className="cursor-pointer hover:bg-blue-100/50 rounded px-0.5 transition-colors duration-200"
            onClick={(e) => handleWordClick(token, e, contextText)}
          >
            {token}
          </span>
        );
      });
    };

    return (text: string) => {
      if (!text) {
        return text;
      }

      if (!activeVocabularyTerms.length) {
        return renderTokens(text, text, 'plain');
      }

      const termsPattern = createFlexiblePattern(activeVocabularyTerms);

      if (!termsPattern) {
        return renderTokens(text, text, 'plain');
      }

      const regex = new RegExp(`\\b(${termsPattern})\\b`, 'gi');
      const parts = text.split(regex);
      const currentSubtitleKey = activeSubtitleId ? `id:${activeSubtitleId}` : `text:${text}`;
      const renderedInCurrentSubtitle = new Set<string>();

      return parts
        .map((part, index) => {
          if (!part) return null;

          const matchedTerm = activeVocabularyTerms.find((entry) => matchesTerm(part, entry));

          if (matchedTerm) {
            const termKey = createVocabularyTermKey(
              matchedTerm.term,
              matchedTerm.scenarioName
            );
            if (!termKey) {
              return renderTokens(part, text, `${index}`);
            }
            const storedKey = highlightedTermsRef.current.get(termKey);
            const alreadyRendered = renderedInCurrentSubtitle.has(termKey);
            const shouldHighlight = (!storedKey || storedKey === currentSubtitleKey) && !alreadyRendered;

            if (shouldHighlight) {
              if (!storedKey) {
                highlightedTermsRef.current.set(termKey, currentSubtitleKey);
              }

              renderedInCurrentSubtitle.add(termKey);

              return (
                <span
                  key={`highlight-${index}`}
                  className={cn(
                    'font-oldschool-grotesk font-[18px] cursor-pointer transition-colors duration-200',
                    matchedTerm.textClass
                  )}
                  onClick={(e) => handleWordClick(part, e, text)}
                >
                  {part}
                </span>
              );
            }
          }

          return renderTokens(part, text, `${index}`);
        })
        .filter(Boolean);
    };
  }, [activeVocabularyTerms, activeSubtitleId, isValidEnglishWord, handleWordClick]);

  // ===== 渲染可交互的字幕文本 =====
  const renderInteractiveSubtitle = useCallback((text: string) => {
    if (!text) {
      return text;
    }

    return highlightTermsInSubtitle(text);
  }, [highlightTermsInSubtitle]);
  
  return (
    <div className="mobile-immersive-content w-full overflow-x-hidden">
      {/* Content container with proper spacing for mobile - increased top margin */}
      <div className="pb-24 w-full"> {/* pt-8 to create space below video player */}
        
        {/* Timeline Vocabulary Cards - Mobile Optimized */}
        {showVocabularyCards ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="px-0 mobile-vocabulary-cards sm:mt-2 mt-0.5"
          >
            <ImmersiveVocabularyCards
              vocabulary={filteredVocabulary}
              currentTime={currentTime}
              onTimestampClick={onTimestampClick}
              onActiveVocabularyTermsChange={onActiveVocabularyTermsChange}
              showTranslation={showTranslation}
              revealAllTranslations={revealAllTranslations}
            />
          </motion.div>
        ) : showLoadingState ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 px-4"
          >
            <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <div className="text-lg font-medium text-muted-foreground mb-2">
              Loading vocabulary cards...
            </div>
            <div className="text-sm text-muted-foreground/70">
              Please wait while we prepare your learning materials
            </div>
          </motion.div>
        ) : showEmptyState ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 px-4"
          >
            <Check className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <div className="text-lg font-medium text-muted-foreground mb-2">
              {t('mobileImmersive.vocabularyAllKnown', 'All caught up!')}
            </div>
            <div className="text-sm text-muted-foreground/70">
              {t(
                'mobileImmersive.vocabularyAllKnownDescription',
                'Swipe right on cards you want to keep practicing and they will appear here.'
              )}
            </div>
          </motion.div>
        ) : null}

        {/* Current Subtitle Display - Mobile Optimized */}
        {showSubtitles && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            className="px-0 sm:px-4 md:px-6 md:pt-6"
          >
            <AnimatePresence mode="wait">
              {(activeSubtitleText?.trim() || activeSubtitleTranslation?.trim()) && (
              <motion.div
                key={`mobile-subtitle-${activeSubtitleText?.slice(0, 30) || 'empty'}-${activeSubtitleTranslation?.slice(0, 30) || 'empty'}`}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ 
                  duration: 0.25,
                  ease: "easeOut",
                  scale: { duration: 0.2 }
                }}
                className="flex justify-center w-full"
              >
                {/* 字幕容器 - 现代化设计 */}
                <div className="w-full max-w-[min(768px,calc(100vw-32px))] px-3 sm:px-4 md:px-6 text-center space-y-1 relative overflow-hidden mx-auto">
                  {/* 装饰性渐变背景 */}
                  <div className="absolute inset-0"></div>
                  
                  {/* 内容区域 */}
                  <div className="relative z-10 space-y-1.5">
                    {/* English Text with enhanced styling */}
                    {activeSubtitleText?.trim() && (
                      <div className="font-oldschool-grotesk text-[19px] md:text-3xl lg:text-3xl font-medium sm:font-semibold text-foreground leading-snug md:leading-normal tracking-wide text-left">
                        {renderInteractiveSubtitle(activeSubtitleText)}
                      </div>
                    )}
                    
                    {/* 分隔线 */}
                    {activeSubtitleText?.trim() && activeSubtitleTranslation?.trim() && showTranslation && (
                      <div className="flex items-center justify-center py-0.5">
                        <div className="h-px w-full bg-gradient-to-r from-transparent via-border/50 to-transparent max-w-[calc(100%-1.5rem)] sm:max-w-[calc(100%-2rem)]"></div>
                      </div>
                    )}
                    
                    {/* Chinese Translation with enhanced styling */}
                    {showTranslation && activeSubtitleTranslation?.trim() && (
                      <div className="text-[16px] md:text-xl text-muted-foreground/90 leading-snug md:leading-relaxed font-light text-center">
                        {activeSubtitleTranslation}
                      </div>
                    )}
                  </div>
                  
                  {/* 底部装饰条 */}
                  {/* <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20"></div> */}
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Transcript Section - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="px-1"
        >
          <TimeDrivenTranscript
            groupedSentences={groupedSentences}
            currentTime={currentTime}
            onSeek={(seconds) => onTimestampClick(seconds)}
            showTranslation={showTranslation}
            isVideoMinimized={false}
            activeVocabularyTerms={activeVocabularyTerms}
            vocabularyTerms={vocabularyTerms}
            episodeId={episodeId}
            onTranscriptUpdate={onTranscriptUpdate}
          />
        </motion.div>

        {/* Debug info (only in development) - Compact version */}
        {/* {process.env.NODE_ENV === 'development' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="fixed bottom-28 right-2 p-2 bg-yellow-50/90 border border-yellow-200 rounded-lg text-xs text-yellow-800 max-w-xs z-50 backdrop-blur-sm"
          >
            <div className="font-medium mb-1">🔍 Mobile Immersive</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span>Time: {Math.round(currentTime)}s</span>
              <span>Vocab: {vocabulary ? '✓' : '✗'}</span>
              <span>Scenarios: {filteredVocabulary?.scenarios?.length || 0}</span>
              <span>Practice: {practiceMode ? practiceModeType : 'Off'}</span>
              <span>Grouped Sentences: {groupedSentences.length}</span>
            </div>
          </motion.div>
        )} */}
      </div>

      {/* Mobile-specific styles for vocabulary cards */}
      <style jsx global>{`
        .mobile-vocabulary-cards .flex-shrink-0 {
          width: calc(100vw - 3rem) !important; /* Full viewport width minus padding */
          max-width: 320px !important; /* Max width for larger phones */
          min-width: 260px !important; /* Min width for smaller phones */
        }
        
        .mobile-vocabulary-cards .flex.overflow-x-auto {
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
          width: 100vw !important; /* Use full viewport width */
          margin-left: calc(-1 * (100vw - 100%) / 2) !important; /* Center align with viewport */
        }
        
        /* Ensure cards container uses full viewport width */
        .mobile-vocabulary-cards .relative.w-full {
          width: 100vw !important;
          margin-left: calc(-1 * (100vw - 100%) / 2) !important;
        }
        
        /* Adjust gap for mobile */
        .mobile-vocabulary-cards .gap-4 {
          gap: 0.75rem !important;
        }
        
        /* Fix container overflow */
        .mobile-vocabulary-cards {
          width: 100vw !important;
          margin-left: calc(-1 * (100vw - 100%) / 2) !important;
          overflow-x: visible !important;
        }
      `}</style>

      {/* Word Drawer for mobile word interaction */}
      {selectedWord && (
        <Drawer open={showWordDrawer} onOpenChange={(open) => {
          setShowWordDrawer(open);
          if (!open) {
            setSelectedWord('');
            setSelectedWordContext('');
            setSelectedStatusValue('NEW');
          }
        }}
        >
          <DrawerContent className="rounded-t-3xl border border-border">
            <DrawerHeader>
              <DrawerTitle className="flex flex-col items-start space-y-1">
                {lookupTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 text-[0.7rem] font-semibold uppercase tracking-wide text-primary/80">
                    {lookupTags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-primary/10 px-1.5 py-0.5"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <span className="flex items-center gap-2">
                  <GraduationCap className="h-6 w-6" />
                  <span className="text-2xl font-bold">{selectedWord}</span>
                </span>
              </DrawerTitle>
              {(explanation?.usphone || translation?.usphone || translation?.phonetic) && (
                <DrawerDescription className="text-base text-start pl-8 font-medium text-stone-700">
                  /{explanation?.usphone || translation?.usphone || translation?.phonetic}/
                </DrawerDescription>
              )}
            </DrawerHeader>
            
            <div className="px-4 pb-4">
              {isWordLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-gray-600">Loading...</span>
                </div>
              ) : (explanation || translation || hasLookupMetadata) ? (
                <div className="space-y-4">
                  {/* 翻译内容 */}
                  {(explanation?.translation || translation?.translation || translation?.explains?.[0]) && (
                    <div className="flex items-start space-x-3">
                      <MessageCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <div className="text-md text-foreground">
                        {explanation?.translation
                          ? (Array.isArray(explanation.translation)
                              ? explanation.translation.map((part: string, index: number) => (
                                  <div key={index} className={index > 0 ? "mt-2" : ""}>{part.trim()}</div>
                                ))
                              : (explanation.translation as string).split(';').map((part, index) => (
                                  <div key={index} className={index > 0 ? "mt-2" : ""}>{part.trim()}</div>
                                )))
                          : (translation?.translation || translation?.explains?.[0])}
                      </div>
                    </div>
                  )}
                  
                  {/* 详细解释 */}
                  {translation?.explains && (translation.explains.length || 0) > 1 && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <div className="text-sm text-gray-600">
                        {translation.explains.slice(1).map((explain, index) => (
                          <div key={index} className={index > 0 ? "mt-1" : ""}>
                            {explain}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasLookupMetadata && (
                    <div className="rounded-lg border border-border/60 bg-muted/40 p-3 text-sm text-foreground/70">
                      {lookupMetadata?.definition && (
                        <p className="whitespace-pre-line text-foreground/80">
                          {lookupMetadata.definition}
                        </p>
                      )}
                      {lookupMetadata?.tag && (
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="rounded bg-secondary/20 px-2 py-0.5 text-secondary-foreground/80">
                            {lookupMetadata.tag}
                          </span>
                        </div>
                      )}
                      {(typeof lookupMetadata?.bnc === 'number' || typeof lookupMetadata?.frq === 'number') && (
                        <div className="mt-2 text-xs text-muted-foreground space-x-3">
                          {typeof lookupMetadata?.bnc === 'number' && (
                            <span>BNC词频排名: {lookupMetadata.bnc}</span>
                          )}
                          {typeof lookupMetadata?.frq === 'number' && (
                            <span>COCA词频排名: {lookupMetadata.frq}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {explanation?.example && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <span className="text-base text-gray-600 italic">
                        {explanation.example}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  {t('vocabulary.noTranslation', 'No translation available')}
                </div>
              )}
            </div>
            
            <DrawerFooter>
              <div className="flex w-full items-stretch gap-1.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'flex-1 min-w-0 justify-between rounded-xl border text-xs h-9 px-3',
                        statusStyles[selectedStatusMobile.value]
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <StatusIconMobile className="h-3.5 w-3.5" />
                        {selectedStatusMobile.label}
                      </span>
                      <span className={cn('h-2 w-2 rounded-full', statusAccentMap[selectedStatusMobile.value])} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {statusOptions.map((option) => {
                      const OptionIcon = option.value === 'MASTERED' ? Check : option.value === 'FUZZY' ? Lightbulb : Sparkles;
                      const isSelected = selectedStatusMobile.value === option.value;
                      return (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={(event) => {
                            event.preventDefault();
                            setSelectedStatusValue(option.value);
                          }}
                          className={cn('flex items-start gap-2', isSelected && 'bg-accent/50 text-foreground')}
                        >
                          <OptionIcon className={cn('h-4 w-4 mt-0.5', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                          <span className="flex flex-col text-sm">
                            <span className="font-medium flex items-center gap-1">
                              {option.label}
                              {isSelected && <Check className="h-3 w-3" />}
                            </span>
                            <span className="text-xs text-muted-foreground">{option.description}</span>
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  className="flex-1 min-w-0 gap-1.5 rounded-xl text-xs h-9 px-3"
                  disabled={
                    !userSettings?.myVocabularySetting ||
                    !selectedWord ||
                    isWordSaving ||
                    isWordSaved ||
                    (!explanation && !translation)
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!selectedWord || isWordSaving || isWordSaved) {
                      return;
                    }

                    const options: any = {
                      episodeId,
                      contextText: selectedWordContext,
                      status: selectedStatusMobile.value,
                    };

                    if (!explanation && translation) {
                      options.youdaoTranslation = {
                        translation: translation.translation,
                        phonetic: translation.phonetic,
                        usphone: translation.usphone,
                        explains: translation.explains,
                      };
                    }

                    saveCard(selectedWord, options);
                  }}
                >
                  {isWordSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isWordSaved ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ClipboardCopy className="h-4 w-4" />
                  )}
                  <span>{isWordSaved ? t('common.saved', 'Saved') : t('common.save')}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 min-w-0 border rounded-xl text-xs h-9 px-3"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (speakerRef?.current) {
                      speakerRef.current.click();
                    }
                  }}
                >
                  <SpeakerIcon
                    divRef={speakerRef}
                    size={9}
                    provider="EdgeTTS"
                    text={selectedWord || ""}
                    lang={getSpeakerLang(userSettings?.languageSetting || "english") as any}
                    voice={undefined}
                    rate={10}
                    volume={100}
                  />
                  <span className="ml-2">{t('common.speak')}</span>
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
